import { useServerFn } from "@tanstack/react-start";
import Uppy, {
  type Body,
  type Meta,
  type UploadResult,
  type UppyFile,
} from "@uppy/core";
import ImageEditor from "@uppy/image-editor";
import Dashboard from "@uppy/react/dashboard";

import "./uploader.css";
import "@uppy/react/css/style.css";
import "@uppy/dashboard/css/style.min.css";
import "@uppy/image-editor/css/style.min.css";
import Tus from "@uppy/tus";
import { useEffect, useRef, useState } from "react";

import { type BucketName, getSignedUploadTokenFn } from "@/data/storage";

import { Button } from "../button";

const STORAGE_URL = `${import.meta.env.VITE_SUPABASE_STORAGE_URL}/storage/v1/object/public`;

export type UploaderProps = {
  bucket: BucketName;
  prefix: string;
  circular?: boolean;
  /** When true (default), the Upload button is disabled until the user edits the image. */
  requireEdit?: boolean;
  initialFiles?: File[];
  onUploadSuccess: (source: string) => void;
  onCancel?: () => void;
  onCancelEdit?: <M extends Meta, B extends Body>(file: UppyFile<M, B>) => void;
};

function createUppy(circular?: boolean) {
  const instance = new Uppy({
    id: "file-uploader",
    restrictions: {
      maxNumberOfFiles: 1,
      allowedFileTypes: ["image/*"],
    },
    autoProceed: false,
  });

  // Workaround for @uppy/dashboard bug: Dashboard.uninstall() calls
  // removePlugin on sub-plugins that may be undefined, causing a crash
  // during componentWillUnmount.
  const originalRemovePlugin = instance.removePlugin.bind(instance);
  instance.removePlugin = (plugin) => {
    if (!plugin) return;
    originalRemovePlugin(plugin);
  };

  instance.use(ImageEditor, {
    cropperOptions: {
      aspectRatio: circular ? 1 : undefined,
      viewMode: 1,
      croppedCanvasOptions: {
        rounded: true,
      },
    },
    actions: {
      revert: true,
      rotate: true,
      granularRotate: true,
      flip: true,
      zoomIn: true,
      zoomOut: true,
      cropSquare: true,
      cropWidescreen: true,
      cropWidescreenVertical: true,
    },
  });

  instance.use(Tus, {
    endpoint: `${import.meta.env.VITE_SUPABASE_STORAGE_URL}/storage/v1/upload/resumable/sign`,
    headers: {
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    uploadDataDuringCreation: true,
    chunkSize: 6 * 1024 * 1024,
    allowedMetaFields: [
      "bucketName",
      "objectName",
      "contentType",
      "cacheControl",
    ],
    onError: (error) => {
      console.log("Failed because: " + error);
    },
  });

  return instance;
}

export function Uploader({
  bucket,
  prefix,
  circular,
  requireEdit = true,
  initialFiles,
  onUploadSuccess,
  onCancelEdit,
  onCancel,
}: UploaderProps) {
  const getSignedUploadToken = useServerFn(getSignedUploadTokenFn);

  const [hasEdited, setHasEdited] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [uppy, setUppy] = useState<Uppy | null>(null);

  const [filesToLoad] = useState(initialFiles);
  const storagePathRef = useRef<string | undefined>();

  // Create and destroy the Uppy instance in an effect so that React
  // StrictMode's mount → unmount → remount cycle gets a fresh instance
  // each time, avoiding stale Dashboard plugin registrations.
  useEffect(() => {
    const instance = createUppy(circular);
    setUppy(instance);

    return () => {
      instance.cancelAll();
      instance.destroy();
    };
  }, [circular]);

  // Event listeners
  useEffect(() => {
    if (!uppy) return;

    const completeHandler = <M extends Meta, B extends Body>(
      result: UploadResult<M, B>,
    ) => {
      if (result.successful?.length && storagePathRef.current) {
        onUploadSuccess(`${STORAGE_URL}/${bucket}/${storagePathRef.current}`);
      }
    };

    const fileAddedHandler = async <M extends Meta, B extends Body>(
      file: UppyFile<M, B>,
    ) => {
      setHasFile(true);
      const { token, storagePath: objectName } = await getSignedUploadToken({
        data: { bucket, prefix, filename: file.name },
      });

      storagePathRef.current = objectName;

      const supabaseMetadata = {
        bucketName: bucket,
        objectName,
        contentType: file.type,
      };

      file.meta = {
        ...file.meta,
        ...supabaseMetadata,
      };

      uppy.setFileState(file.id, {
        tus: {
          headers: {
            "x-signature": token,
          },
        },
        meta: file.meta,
        type: file.type,
      });
    };

    const startHandler = <M extends Meta, B extends Body>(
      _: UppyFile<M, B>[],
    ) => {
      setUploading(true);
    };

    const editCompleteHandler = <M extends Meta, B extends Body>(
      _: UppyFile<M, B>,
    ) => {
      setHasEdited(true);
    };

    const fileRemovedHandler = <M extends Meta, B extends Body>(
      _: UppyFile<M, B>,
    ) => {
      if (onCancel && uppy.getFiles().length === 0) {
        onCancel();
      }
    };

    uppy.on("file-added", fileAddedHandler);
    uppy.on("file-removed", fileRemovedHandler);
    uppy.on("upload-start", startHandler);
    uppy.on("file-editor:complete", editCompleteHandler);
    uppy.on("complete", completeHandler);

    if (onCancelEdit) {
      uppy.on("file-editor:cancel", onCancelEdit);
    }

    return () => {
      uppy.off("file-added", fileAddedHandler);
      uppy.off("file-removed", fileRemovedHandler);
      uppy.off("upload-start", startHandler);
      uppy.off("file-editor:complete", editCompleteHandler);
      uppy.off("complete", completeHandler);

      if (onCancelEdit) {
        uppy.off("file-editor:cancel", onCancelEdit);
      }
    };
  }, [
    uppy,
    getSignedUploadToken,
    bucket,
    prefix,
    onUploadSuccess,
    onCancel,
    onCancelEdit,
  ]);

  // Load initial files once uppy is ready
  useEffect(() => {
    if (uppy && filesToLoad?.length) {
      uppy.addFiles(
        filesToLoad.map((file) => ({
          name: file.name,
          type: file.type,
          data: file,
        })),
      );

      // autoOpen="imageEditor" doesn't reliably fire for programmatically
      // added files. Open the editor explicitly after a frame to ensure the
      // Dashboard plugin is fully mounted.
      const raf = requestAnimationFrame(() => {
        const firstFile = uppy.getFiles()[0];
        if (firstFile) {
          const dashboard = uppy.getPlugin("Dashboard");
          (
            dashboard as { openFileEditor?: (f: typeof firstFile) => void }
          )?.openFileEditor?.(firstFile);
        }
      });

      return () => cancelAnimationFrame(raf);
    }
  }, [uppy, filesToLoad]);

  if (!uppy) return null;

  return (
    <div className="flex flex-col gap-2">
      <Dashboard
        className={circular ? "circle" : undefined}
        uppy={uppy}
        autoOpen="imageEditor"
        height={450}
        note="Supported formats: jpg, png."
        showLinkToFileUploadResult={false}
        showRemoveButtonAfterComplete={false}
        hideCancelButton={true}
        hidePauseResumeButton={true}
        hideUploadButton={true}
        proudlyDisplayPoweredByUppy={false}
      />
      <div className="flex flex-row justify-end gap-2">
        <Button slot={onCancel ? "close" : undefined} onPress={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          isDisabled={(requireEdit ? !hasEdited : !hasFile) || isUploading}
          onPress={() => {
            uppy.upload();
          }}
        >
          Upload
        </Button>
      </div>
    </div>
  );
}
